import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import SpeciaLogo from "@/components/SpeciaLogo";
import NotificationBell from "@/components/NotificationBell";
import { LogOut, LayoutDashboard, Settings2, ShieldCheck, UserCog } from "lucide-react";
import api from "@/lib/api";

export default function Navbar() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const marketing = ["/", "/pricing"].includes(pathname);
  if (pathname.startsWith("/share/")) return null;
  const linkCls = "text-[#3A2DB8]/80 hover:text-[#FF6B3D] font-medium transition-colors";

  const exitImpersonation = async () => {
    const { data } = await api.post("/auth/impersonation/stop");
    setUser(data);
    navigate("/admin");
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-border">
      {user?.impersonated_by && (
        <div className="bg-[#FF6B3D] text-white text-xs font-semibold px-6 py-1.5 flex items-center justify-center gap-3" data-testid="impersonation-banner">
          <UserCog className="w-3.5 h-3.5" /> Viewing as customer <span className="font-mono">{user.email}</span> (admin: {user.impersonated_by})
          <button onClick={exitImpersonation} className="underline underline-offset-2 hover:text-[#1E1560]" data-testid="impersonation-exit-btn">Exit to admin</button>
        </div>
      )}
      <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
        <SpeciaLogo />

        {marketing && (
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="/#model" className={linkCls} data-testid="nav-model">The Model</a>
            <a href="/#platforms" className={linkCls} data-testid="nav-platforms">Platforms</a>
            <Link to="/pricing" className={linkCls} data-testid="nav-pricing">Pricing</Link>
            <a href="/#contact" className={linkCls} data-testid="nav-contact">Contact</a>
          </nav>
        )}

        <div className="flex items-center gap-2">
          {user === null && (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn"
                className="rounded-full text-[#2A1E9E] hover:bg-[#3A2DB8]/10">
                Sign in
              </Button>
              <Button onClick={() => navigate("/register")} data-testid="nav-register-btn"
                className="rounded-full bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold px-5 glow-primary">
                Get started
              </Button>
            </>
          )}
          {user && (
            <>
              {user.role === "admin" ? (
                <>
                  <Button variant="ghost" onClick={() => navigate("/admin")} data-testid="nav-admin-btn" className="rounded-full gap-2 text-[#2A1E9E] hover:bg-[#3A2DB8]/10">
                    <ShieldCheck className="w-4 h-4" /> Admin
                  </Button>
                  <NotificationBell />
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-btn" className="rounded-full gap-2 text-[#2A1E9E] hover:bg-[#3A2DB8]/10">
                    <LayoutDashboard className="w-4 h-4" /> Dashboard
                  </Button>
                  <Button variant="ghost" onClick={() => navigate("/config")} data-testid="nav-config-btn" className="rounded-full gap-2 hidden sm:flex text-[#2A1E9E] hover:bg-[#3A2DB8]/10">
                    <Settings2 className="w-4 h-4" /> Environments
                  </Button>
                  <NotificationBell />
                </>
              )}
              <Button variant="outline" onClick={onLogout} data-testid="nav-logout-btn" className="rounded-full gap-2 border-[#3A2DB8]/30 text-[#2A1E9E] bg-white/70 hover:bg-white">
                <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
